import { Box } from '@mui/material'
import { Page } from 'Common/Page/Page'
import { PageHeader, Button } from 'Common/Supplyvue-ui'
import { useState } from 'react'
import { PlanDataTypes } from 'Features/OperationalPlanner/OperationalPlanner.types'
import { toast, CheckmarkIcon } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import {
  useCreateOperationalPlan,
  useSourceSites,
} from 'Features/OperationalPlanner/OperationalPlan.data'
import { WorkcentreModal } from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.view'
import {
  SourceSiteDetails,
  SelectedWorkcentreDetails,
} from 'Features/OperationalPlanner/WorkcentreModal/WorkcentreModal.types'
import { Loader } from 'Common/Loader/Loader'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'
import { getApiErrorMessage } from 'Features/auth/axios-client'
import { PLAN_ACCESS_TYPE } from 'Common/Constants/Constants'
import { startOfWeek } from 'date-fns'
import { ScenarioPlanDataType } from './CreateScenarioPlan.types'
import {
  CreatePlanCalendar,
  CreatePlanTextBox,
  CreatePlanAccess,
  CreatePlanButtonGroup,
  CreatePlanSelect,
} from 'Common/Components/CreatePlan.components'
import { useProjects, useScenarios } from './CreateScenarioPlan.data'
import { validator } from '@exodus/schemasafe'
import { CREATE_OPERATIONAL_PLAN_PAYLOAD } from './CreateScenarioPlan.payload'

const tenant = localStorage.getItem('tenantId') ?? ''

const ScenarioPlanData = {
  planStartWeek: startOfWeek(new Date(), { weekStartsOn: 1 }),
  templateStartDateKey: startOfWeek(new Date(), { weekStartsOn: 1 }),
  planDescription: '',
  planHorizon: 0,
  constrainingHorizon: 0,
  sourceSites: [],
  planAccessType: PLAN_ACCESS_TYPE.PRIVATE,
  planType: 'scenario',
  projectId: '',
  scenarioId: '',
}

const runPlanParams = {
  extractData: true,
  unconstrainedPlan: true,
}

export const CreateScenarioPlanPage = () => {
  useBreadcrumbsData({
    plan_id: '',
  })
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [planData, setPlanData] =
    useState<ScenarioPlanDataType>(ScenarioPlanData)
  const [isRunPlan, setIsRunPlan] = useState<boolean>(false)
  const [isEditSettings, setIsEditSettings] = useState<boolean>(false)
  const navigate = useNavigate()
  const handleActionClick = () => {
    setIsModalOpen(true)
  }

  const { data: sourceSiteData, isLoading: isSourceSitesLoading } =
    useSourceSites(tenant)

  const { data: projects, isLoading: isProjectsLoading } = useProjects()

  const { data: scenarios, isLoading: isScenariosLoading } = useScenarios(
    planData.projectId
  )

  const handleEditSettings = () => {
    setIsEditSettings(true)
    prepareAndSubmitPlanData(false)
  }

  const handleRunPlan = () => {
    setIsRunPlan(true)
    prepareAndSubmitPlanData(true)
  }

  const formatDate = (date: Date) => {
    const month = date.getMonth() + 1
    const day = date.getDate()
    const monthFormatted = month < 10 ? `0${month}` : month
    const dayFormatted = day < 10 ? `0${day}` : day
    return `${date.getFullYear()}-${monthFormatted}-${dayFormatted}`
  }

  const prepareAndSubmitPlanData = (isUnConstrainedPlan: boolean) => {
    if (
      planData.planStartWeek instanceof Date &&
      planData.templateStartDateKey instanceof Date
    ) {
      const validate = validator(CREATE_OPERATIONAL_PLAN_PAYLOAD)
      const dataToServer = {
        ...planData,
        planHorizon: parseInt(planData.planHorizon.toString()),
        constrainingHorizon: parseInt(planData.constrainingHorizon.toString()),
        planStartWeek: formatDate(planData.planStartWeek),
        templateStartDateKey: formatDate(planData.templateStartDateKey),
        extractData: runPlanParams.extractData,
        unconstrainedPlan: isUnConstrainedPlan,
      }

      if (validate(dataToServer)) {
        submitCreatePlan(dataToServer)
      }
    }
  }

  const submitCreatePlan = (planData: PlanDataTypes) => {
    createOperationalPlan(planData)
  }

  const onSuccessfulPlanCreation = (
    planId: string,
    unconstrainedPlan: boolean
  ) => {
    toast.success('Plan created successfully')

    if (unconstrainedPlan) {
      navigate(`/`)
    } else {
      navigate(`/plan/${planId}/edit`)
    }
  }

  const onFailedPlanCreation = (error?: unknown) => {
    setIsRunPlan(false)
    toast.error(getApiErrorMessage(error, 'Plan creation failed'))
  }

  const { mutate: createOperationalPlan, isLoading: isPlanCreating } =
    useCreateOperationalPlan({
      onSuccessfulPlanCreation,
      onFailedPlanCreation,
    })

  const getSelectedSites = (sourceSiteData: SourceSiteDetails[]) => {
    const selectedSourceSites = sourceSiteData
      .filter((sourceSite) => sourceSite.isSelected)
      .map((sourceSite) => {
        const workcentres = sourceSite.siteWorkcentres
          .filter((workcentre) => workcentre.isSelected)
          .map((workcentre) => workcentre)
        return {
          siteKey: sourceSite.siteKey,
          siteCode: sourceSite.siteCode,
          siteLabel: sourceSite.siteLabel,
          siteWorkcentres: workcentres,
        }
      })
    setPlanValue('sourceSites', selectedSourceSites)
    setIsModalOpen(false)
  }

  const setPlanValue = (
    planDataKey: string,
    value: string | number | Date | SelectedWorkcentreDetails[]
  ) => {
    setPlanData((prevState: ScenarioPlanDataType) => ({
      ...prevState,
      [planDataKey]: value,
    }))
  }

  if (isSourceSitesLoading) {
    return <Loader />
  }

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Create scenario plans" />
        <Box display="flex" flexDirection="column" flex={1} alignItems="center">
          <Box width="30%">
            {/* This is the create operational plan page */}
            <Box sx={{ minWidth: '400px' }}>
              <Box display="flex" flexDirection="column">
                <CreatePlanCalendar
                  setPlanValue={setPlanValue}
                  planDataKey="planStartWeek"
                  value={planData.planStartWeek}
                  disabled={isPlanCreating}
                  label="Plan start week"
                />

                {planData.planStartWeek &&
                  planData.planStartWeek?.getDay() !== 1 && (
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Box display="flex" alignItems="center" width="30%"></Box>
                      <Box color="red" marginLeft={2}>
                        Please make sure the selected day is a Monday.
                      </Box>
                      <Box display="flex" alignItems="center" width="10%"></Box>
                    </Box>
                  )}
                <Box marginTop={2}></Box>
                <CreatePlanCalendar
                  setPlanValue={setPlanValue}
                  planDataKey="templateStartDateKey"
                  value={planData.templateStartDateKey}
                  disabled={isPlanCreating}
                  label="Template start week"
                />

                {planData.templateStartDateKey &&
                  planData.templateStartDateKey?.getDay() !== 1 && (
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                    >
                      <Box display="flex" alignItems="center" width="30%"></Box>
                      <Box color="red" marginLeft={2}>
                        Please make sure the selected day is a Monday.
                      </Box>
                      <Box display="flex" alignItems="center" width="10%"></Box>
                    </Box>
                  )}
                <CreatePlanTextBox
                  label="Plan description"
                  setPlanValue={setPlanValue}
                  value={planData.planDescription}
                  planDataKey="planDescription"
                  disabled={isPlanCreating}
                />

                <CreatePlanTextBox
                  label="Plan horizon"
                  setPlanValue={setPlanValue}
                  value={planData.planHorizon}
                  planDataKey="planHorizon"
                  disabled={isPlanCreating}
                  inputType="number"
                />

                <CreatePlanTextBox
                  label="Constraining horizon"
                  setPlanValue={setPlanValue}
                  value={planData.constrainingHorizon}
                  planDataKey="constrainingHorizon"
                  disabled={isPlanCreating}
                  inputType="number"
                />
                <CreatePlanSelect
                  data={projects}
                  label="project"
                  setPlanValue={setPlanValue}
                  planDataKey="projectId"
                  value={planData.projectId}
                  disabled={isPlanCreating}
                  isLoading={isProjectsLoading}
                />
              </Box>

              <CreatePlanSelect
                data={scenarios ?? []}
                label="Scenario"
                setPlanValue={setPlanValue}
                planDataKey="scenarioId"
                value={planData.scenarioId}
                disabled={isPlanCreating || !planData.projectId}
                isLoading={isScenariosLoading}
              />

              <CreatePlanAccess
                setPlanValue={setPlanValue}
                label="Plan access"
                disabled={isPlanCreating}
                planDataKey="planAccessType"
                value={planData.planAccessType}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-around',
                marginTop: '20px',
                marginLeft: '70px',
              }}
            >
              <Box display="flex" flexDirection="column">
                <Box marginBottom={2}>
                  <Button
                    onClick={handleActionClick}
                    sx={{ marginLeft: '20px', minWidth: '105px' }}
                    label="Create scenario plan"
                    disabled={
                      planData.planStartWeek?.getDay() !== 1 ||
                      planData.templateStartDateKey?.getDay() !== 1 ||
                      planData.planHorizon <= 0 ||
                      !planData.projectId ||
                      !planData.scenarioId ||
                      isPlanCreating
                    }
                    variant="contained"
                  >
                    {planData.sourceSites.length > 0 && (
                      <Box marginRight={1}>
                        <CheckmarkIcon />
                      </Box>
                    )}
                    Select source sites
                  </Button>
                </Box>
                {planData.sourceSites.length > 0 && (
                  <CreatePlanButtonGroup
                    isEditSettings={isEditSettings}
                    handleRunPlan={handleRunPlan}
                    disabled={
                      !(
                        (planData.planStartWeek && planData.planHorizon > 0) ||
                        isPlanCreating
                      )
                    }
                    isRunPlan={isRunPlan}
                    isPlanCreating={isPlanCreating}
                    handleEditSettings={handleEditSettings}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <WorkcentreModal
          isModalOpen={isModalOpen}
          handleModalClose={() => setIsModalOpen(false)}
          handleOkbuttonClick={getSelectedSites}
          isUpdating={false}
          readOnly={false}
          sourceSiteData={sourceSiteData ?? []}
          templateSourceSiteDetails={planData.sourceSites ?? []}
        />
      </Box>
    </Page>
  )
}
